import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { ChevronLeft, ChevronRight, Calendar, CheckSquare } from 'lucide-react'
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths, isToday } from 'date-fns'
import { es } from 'date-fns/locale'

const STATUS_COLOR = {
  pending:     'bg-gray-400',
  in_progress: 'bg-blue-500',
  review:      'bg-purple-500',
  completed:   'bg-green-500',
  blocked:     'bg-red-500',
}

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [tasks, setTasks]               = useState([])
  const [selectedDay, setSelectedDay]   = useState(null)
  const [loading, setLoading]           = useState(true)
  const navigate = useNavigate()

  useEffect(() => { fetchTasks() }, [currentMonth])

  async function fetchTasks() {
    setLoading(true)
    const monthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd')
    const monthEnd   = format(endOfMonth(currentMonth),   'yyyy-MM-dd')

    const { data } = await supabase
      .from('tasks')
      .select('id, name, status, due_date, sections(id, name, phase:phases(id, name, consultancy_id, consultancy:consultancies(id, name)))')
      .gte('due_date', monthStart)
      .lte('due_date', monthEnd)
      .order('due_date')

    setTasks(data ?? [])
    setLoading(false)
  }

  // Build calendar grid
  const monthStart  = startOfMonth(currentMonth)
  const monthEnd    = endOfMonth(currentMonth)
  const calStart    = startOfWeek(monthStart, { weekStartsOn: 1 })
  const calEnd      = endOfWeek(monthEnd, { weekStartsOn: 1 })

  const weeks = []
  let day = calStart
  while (day <= calEnd) {
    const week = []
    for (let i = 0; i < 7; i++) {
      week.push(day)
      day = addDays(day, 1)
    }
    weeks.push(week)
  }

  function tasksForDay(d) {
    return tasks.filter(t => isSameDay(new Date(t.due_date + 'T00:00:00'), d))
  }

  const selectedTasks = selectedDay ? tasksForDay(selectedDay) : []

  function navigateToTask(task) {
    const phase = task.sections?.phase
    const consId = phase?.consultancy_id
    if (consId) navigate(`/consultancy/${consId}`)
  }

  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar size={22} className="text-brand-600" />
            Calendario
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Tareas con fecha límite</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentMonth(m => subMonths(m, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronLeft size={18} className="text-gray-600" />
          </button>
          <span className="text-sm font-semibold text-gray-800 w-36 text-center capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </span>
          <button onClick={() => setCurrentMonth(m => addMonths(m, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronRight size={18} className="text-gray-600" />
          </button>
          <button onClick={() => setCurrentMonth(new Date())}
            className="ml-1 px-3 py-1.5 text-xs font-medium bg-brand-50 text-brand-700 hover:bg-brand-100 rounded-lg transition-colors">
            Hoy
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar grid */}
        <div className="flex-1 card p-0 overflow-hidden">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-gray-100">
            {weekDays.map(d => (
              <div key={d} className="py-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
            </div>
          ) : (
            <div>
              {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 border-b border-gray-50 last:border-0">
                  {week.map((d, di) => {
                    const dayTasks   = tasksForDay(d)
                    const inMonth    = isSameMonth(d, currentMonth)
                    const isSelected = selectedDay && isSameDay(d, selectedDay)
                    const today      = isToday(d)

                    return (
                      <div
                        key={di}
                        onClick={() => setSelectedDay(isSameDay(d, selectedDay) ? null : d)}
                        className={`min-h-[80px] p-1.5 border-r border-gray-50 last:border-0 cursor-pointer transition-colors
                          ${!inMonth ? 'bg-gray-50/50' : ''}
                          ${isSelected ? 'bg-brand-50' : 'hover:bg-gray-50'}
                        `}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mb-1 mx-auto
                          ${today ? 'bg-brand-700 text-white' : inMonth ? 'text-gray-700' : 'text-gray-300'}
                        `}>
                          {format(d, 'd')}
                        </div>
                        <div className="space-y-0.5">
                          {dayTasks.slice(0, 3).map(t => (
                            <div key={t.id}
                              className={`text-xs px-1 py-0.5 rounded truncate flex items-center gap-1
                                ${t.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-brand-100 text-brand-700'}
                              `}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_COLOR[t.status]}`} />
                              <span className="truncate">{t.name}</span>
                            </div>
                          ))}
                          {dayTasks.length > 3 && (
                            <div className="text-xs text-gray-400 pl-1">+{dayTasks.length - 3} más</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Day detail panel */}
        <div className="lg:w-72 shrink-0">
          {selectedDay ? (
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center font-bold">
                  {format(selectedDay, 'd')}
                </div>
                <div>
                  <p className="font-semibold text-gray-800 capitalize">
                    {format(selectedDay, 'EEEE', { locale: es })}
                  </p>
                  <p className="text-xs text-gray-400 capitalize">
                    {format(selectedDay, 'MMMM yyyy', { locale: es })}
                  </p>
                </div>
              </div>

              {selectedTasks.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Sin tareas este día</p>
              ) : (
                <div className="space-y-2">
                  {selectedTasks.map(t => (
                    <button
                      key={t.id}
                      onClick={() => navigateToTask(t)}
                      className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50/50 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${STATUS_COLOR[t.status]}`} />
                        <div>
                          <p className={`text-sm font-medium ${t.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                            {t.name}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {t.sections?.phase?.consultancy?.name}
                          </p>
                          <p className="text-xs text-gray-400">{t.sections?.name}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="card text-center text-gray-400">
              <Calendar size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Haz clic en un día para ver sus tareas</p>
              <p className="text-xs mt-2">
                {tasks.length > 0
                  ? `${tasks.length} tarea${tasks.length !== 1 ? 's' : ''} este mes`
                  : 'Sin tareas este mes'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
